import { useEffect, useMemo, useState } from "react";
import { isAxiosError } from "axios";
import { Alert, Center, Loader, Paper, Select, Stack, Text, Title } from "@mantine/core";
import { InputDropdown } from "@onengine/component-library";
import { IconCircleCheckFilled, IconSend } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { accountsPayableEndpoints } from "@/api/accounts-payable/accountsPayable.endpoints";
import { apPublicAxios } from "@/api/accounts-payable/accountsPayable.publicClient";
import { Button } from "@/components/ui/Button/Button";
import { Input } from "@/components/ui/Input/Input";
import { validatePaymentFieldValue } from "@/lib/paymentFieldRules";
import { suggestPaymentLinkAchSecCode } from "@/api/accounts-payable/achSecCode.api";
import { useAchSecCodeSuggestion } from "@/hooks/accounts-payable/useAchSecCodeSuggestion";
import {
  ACCOUNT_HOLDER_NAME_FIELD_KEY,
  ACH_SEC_CODE_FIELD_KEY,
  paymentFieldOptionsFor,
  paymentFieldRulesFor,
  secCodeOwnerAfterEdit,
  withPaymentFieldChange,
  type SecCodeOwner,
} from "@/lib/paymentFieldOptions";
import { getApiErrorMessage } from "@/utils/responseHandler";

import type {
  SpPaymentLinkInfo,
  SpPaymentLinkInfoResponse,
  SpPaymentLinkMethod,
  SpPaymentLinkMethodField,
} from "@/api/accounts-payable/accountsPayable.types";
import type { ApiErrorResponse } from "@/types/common.types";

// Public, token-gated page -- providers open it from the SMS link with no session, so it
// deliberately has no auth guard and uses the bare apPublicAxios client (no auth
// interceptors / 401 redirects).
export const Route = createFileRoute("/sp-payment/$token/")({
  component: PublicSpPaymentPage,
});

const SUBMIT_FAILED_MESSAGE = "We couldn't save your details. Please try again.";
const LINK_UNVERIFIED_MESSAGE =
  "This link could not be checked right now. Please try again in a moment.";
const NO_METHODS_MESSAGE =
  "This sender has not set up any payment methods yet, so there is nothing to fill in. Please contact them.";

/** Mirrors fieldPlaceholder on the operator's form: the description doubles as the hint. */
const fieldPlaceholder = (field: SpPaymentLinkMethodField): string =>
  field.description?.trim() || "Enter";

const getLinkInfo = async (token: string): Promise<SpPaymentLinkInfo> => {
  const response = await apPublicAxios.get<SpPaymentLinkInfoResponse>(
    accountsPayableEndpoints.publicPaymentLinkInfo(token),
  );
  if (!response.data?.success || !response.data.data) {
    throw new Error("This payment link could not be verified.");
  }
  return response.data.data;
};

// A provider on this page has no session and no support context, so a dead link has to say
// so instead of inviting an endless retry. Only the API envelope's own message is safe to
// show: getApiErrorMessage otherwise falls through to the axios transport string
// ("Request failed with status code 500").
const getEnvelopeMessage = (error: unknown, fallback: string): string => {
  if (!isAxiosError<ApiErrorResponse>(error) || !error.response?.data?.error?.message) {
    return fallback;
  }
  return getApiErrorMessage(error, fallback);
};

const getSubmitErrorMessage = (error: unknown): string =>
  getEnvelopeMessage(error, SUBMIT_FAILED_MESSAGE);

// The provider has not typed anything yet on this branch, so the save-failed
// fallback would tell them their details were lost when nothing was ever sent.
const getLinkErrorMessage = (error: unknown): string =>
  getEnvelopeMessage(error, LINK_UNVERIFIED_MESSAGE);

function PublicSpPaymentPage() {
  const { token } = Route.useParams();
  const [methodId, setMethodId] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [secCodeOwner, setSecCodeOwner] = useState<SecCodeOwner>("model");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isDone, setIsDone] = useState(false);

  const {
    data: info,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["spPaymentPublicLink", token],
    queryFn: () => getLinkInfo(token),
    retry: 1,
  });

  // A body that is not a list of methods is no methods, not a reason to map over
  // it: this is an API boundary on a page an unauthenticated provider reaches.
  const methods: SpPaymentLinkMethod[] = useMemo(
    () => (Array.isArray(info?.paymentMethods) ? info.paymentMethods : []),
    [info?.paymentMethods],
  );

  const methodOptions = useMemo(
    () => methods.map((method) => ({ value: method.id, label: method.name })),
    [methods],
  );

  // Nothing is preselected. The provider chooses the method deliberately rather
  // than being defaulted onto whichever the entity happens to have sorted first.
  const selectedMethod = methods.find((method) => method.id === methodId);
  const fields = selectedMethod?.fields ?? [];

  const missingRequired = fields.some((field) => field.isRequired && !values[field.key]?.trim());

  // The same rule the server enforces, shown here so a provider is told what is
  // wrong while they are still on the form. The server check is the real one:
  // this page is unauthenticated, so nothing it does can be relied on.
  //
  // Only fields the provider has actually typed into are reported. Flagging an
  // untouched field turns the whole form red before anyone has done anything.
  const fieldErrors = useMemo(() => {
    const errors: Record<string, string> = {};
    for (const field of fields) {
      const value = values[field.key] ?? "";
      if (value.trim().length === 0) continue;
      const message = validatePaymentFieldValue(
        {
          label: field.label,
          isRequired: field.isRequired,
          rules: paymentFieldRulesFor(field),
        },
        value,
      );
      if (message) errors[field.key] = message;
    }
    return errors;
  }, [fields, values]);

  const hasSecCodeField = fields.some((field) => field.key === ACH_SEC_CODE_FIELD_KEY);

  const linkSecCodeFetcher = useMemo(() => suggestPaymentLinkAchSecCode(token), [token]);
  const modelSecCode = useAchSecCodeSuggestion(values[ACCOUNT_HOLDER_NAME_FIELD_KEY] ?? "", {
    scope: `link:${token}`,
    fetcher: linkSecCodeFetcher,
    enabled: hasSecCodeField && secCodeOwner === "model",
  });

  useEffect(() => {
    if (!modelSecCode) return;
    setValues((current) =>
      (current[ACCOUNT_HOLDER_NAME_FIELD_KEY] ?? "").trim() === modelSecCode.forName &&
      current[ACH_SEC_CODE_FIELD_KEY] !== modelSecCode.secCode
        ? { ...current, [ACH_SEC_CODE_FIELD_KEY]: modelSecCode.secCode }
        : current,
    );
  }, [modelSecCode?.secCode, modelSecCode?.forName]);

  const setFieldValue = (key: string, value: string) => {
    const nextOwner = secCodeOwnerAfterEdit(secCodeOwner, key);
    setSecCodeOwner(nextOwner);
    setValues((current) =>
      withPaymentFieldChange(current, key, value, { secCodeOwner: nextOwner, hasSecCodeField }),
    );
  };

  const hasFieldErrors = Object.keys(fieldErrors).length > 0;
  const canSubmit = Boolean(selectedMethod) && !missingRequired && !hasFieldErrors && !isSubmitting;

  const handleSubmit = async () => {
    if (!canSubmit || !selectedMethod) return;
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      // Blank optional fields are dropped rather than sent as empty strings, so a
      // skipped field reads as absent to the reviewer instead of as a cleared value.
      const collectedFields = Object.fromEntries(
        fields
          .map((field) => [field.key, values[field.key]?.trim() ?? ""] as const)
          .filter(([, value]) => value.length > 0),
      );
      await apPublicAxios.post(accountsPayableEndpoints.publicPaymentLinkSubmit(token), {
        paymentMethodDefId: selectedMethod.id,
        collectedFields,
      });
      setIsDone(true);
    } catch (submitFailure) {
      setSubmitError(getSubmitErrorMessage(submitFailure));
    } finally {
      setIsSubmitting(false);
    }
  };

  const senderName = info?.entityName ?? "the sender";

  return (
    <Center mih="100vh" bg="var(--background-secondary, #f4f6f8)" p="var(--spacing-medium)">
      <Paper shadow="md" radius="lg" p="xl" w="min(520px, 100%)">
        {isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : isError || !info ? (
          <Stack gap="var(--spacing-small)">
            <Title order={3}>We couldn’t verify this link</Title>
            <Text size="sm" c="dimmed">
              {getLinkErrorMessage(error)}
            </Text>
            <Text size="sm" c="dimmed">
              Please check the link and try again, or contact the sender for a new one.
            </Text>
          </Stack>
        ) : isDone ? (
          <Stack align="center" gap="var(--spacing-small)">
            <IconCircleCheckFilled size={48} color="var(--icon-success-default, #12A150)" />
            <Title order={3}>Thank you — we’ve got it</Title>
            <Text size="sm" c="dimmed" ta="center">
              Your payment details have been sent to {senderName} for review. You can safely close
              this page.
            </Text>
          </Stack>
        ) : (
          <Stack gap="var(--spacing-medium)">
            <Stack gap="var(--spacing-2x-small)">
              <Title order={3}>Hi {info.providerName || "there"},</Title>
              <Text size="sm" c="dimmed">
                {senderName} needs your payment details so they can pay you.
              </Text>
              {info.expiresAt && (
                <Text size="xs" c="dimmed">
                  This link expires on {new Date(info.expiresAt).toLocaleString()}.
                </Text>
              )}
            </Stack>

            {methods.length === 0 ? (
              <Text size="sm" c="dimmed">
                {NO_METHODS_MESSAGE}
              </Text>
            ) : (
              <>
                <Select
                  label="How would you like to be paid?"
                  placeholder="Select a payment method"
                  withAsterisk
                  data={methodOptions}
                  value={methodId}
                  onChange={(next) => {
                    // Values are cleared on switch rather than carried across: two
                    // methods rarely share a field, and anything kept would be
                    // submitted against a method that never asked for it.
                    setMethodId(next);
                    setValues({});
                    setSecCodeOwner("model");
                  }}
                  size="md"
                  allowDeselect={false}
                  comboboxProps={{ withinPortal: false }}
                />

                {selectedMethod?.description && (
                  <Text size="xs" c="dimmed">
                    {selectedMethod.description}
                  </Text>
                )}

                {fields.map((field) => {
                  const options = paymentFieldOptionsFor(field.key);
                  return options ? (
                    <InputDropdown
                      key={`${selectedMethod?.id}:${field.key}`}
                      mode="single"
                      label={field.label}
                      placeholder="Select"
                      optional={!field.isRequired}
                      items={options}
                      value={values[field.key] || null}
                      error={fieldErrors[field.key]}
                      onChange={(next) => setFieldValue(field.key, next ?? "")}
                      size="large"
                    />
                  ) : (
                    <Input
                      key={`${selectedMethod?.id}:${field.key}`}
                      label={field.label}
                      placeholder={fieldPlaceholder(field)}
                      required={field.isRequired}
                      optional={!field.isRequired}
                      autoComplete="off"
                      spellCheck={false}
                      value={values[field.key] ?? ""}
                      error={fieldErrors[field.key]}
                      onChange={(next) => setFieldValue(field.key, next)}
                      size="large"
                    />
                  );
                })}

                {submitError && (
                  <Alert color="red" role="alert" aria-live="polite">
                    {submitError}
                  </Alert>
                )}

                <Button
                  variant="primary"
                  iconLeft={<IconSend size={16} />}
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  isDisabled={!canSubmit}
                >
                  Send My Payment Details
                </Button>

                {!selectedMethod && (
                  <Text size="xs" c="dimmed">
                    Choose how you would like to be paid to continue.
                  </Text>
                )}
                {selectedMethod && missingRequired && (
                  <Text size="xs" c="dimmed">
                    Fill in every required field to send your details.
                  </Text>
                )}

                <Text size="xs" c="dimmed">
                  {senderName} will review these before any payment is made.
                </Text>
              </>
            )}
          </Stack>
        )}
      </Paper>
    </Center>
  );
}
